import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  IconButton,
  Checkbox,
  FormControlLabel,
  TableSortLabel,

  Card,
  CardContent,
  Collapse,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AutoAwesome as GenerateIcon,
  ChatBubbleOutline as CommentIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { ActionItem, SubTask, aiBuilderAPI, userAPI, User, ActionItemsCompletionSummary } from '../services/api';
import SubTasksTable from './SubTasksTable';
import CommentSection from './CommentSection';
import LinearProgress from '@mui/material/LinearProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { useAuth } from '../contexts/AuthContext';

interface ActionItemsManagementProps {
  companyId: number;
  companyName: string;
  actionItems: ActionItem[];
  onActionItemUpdate: () => void;
  completionSummary?: ActionItemsCompletionSummary;
  isPrimaryUser?: boolean;  // Can delete action items (client admin, consultant admin, or superuser)
  assignableUsers?: User[];  // Optional - if provided, skips fetching
}

interface EditableActionItem extends ActionItem {
  isEditing?: boolean;
  editedValues?: Partial<ActionItem>;
  expanded?: boolean;
}

type SortField = 'title' | 'status' | 'priority' | 'assigned_to_name' | 'start_date' | 'due_date' | 'created_at';
type SortDirection = 'asc' | 'desc';

const ActionItemsManagement: React.FC<ActionItemsManagementProps> = ({
  companyId,
  companyName,
  actionItems,
  onActionItemUpdate,
  completionSummary,
  isPrimaryUser = false,
  assignableUsers: propAssignableUsers,
}) => {
  const { user: currentUser } = useAuth();
  const [items, setItems] = useState<EditableActionItem[]>(actionItems);
  const [showCompleted, setShowCompleted] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState<User[]>(propAssignableUsers || []);
  
  // User filter state - filters tasks/subtasks assigned to a specific user
  const [userFilter, setUserFilter] = useState<number | ''>('');
  
  // Sorting state
  const [sortField, setSortField] = useState<SortField>('title');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    assignedTo: '',
    audit: '',
    priority: '',
  });

  // Expansion state
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [expandedComments, setExpandedComments] = useState<Set<number>>(new Set());
  const [expandedAuditSections, setExpandedAuditSections] = useState<Set<string>>(new Set());
  const [generatingItems, setGeneratingItems] = useState<Set<number>>(new Set());
  const [commentUpdateTrigger, setCommentUpdateTrigger] = useState(0);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetAudit, setDeleteTargetAudit] = useState<{ sessionId: number; auditName: string; itemCount: number } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load assignable users for this company (only if not provided via props)
  useEffect(() => {
    // If users were provided via props (even empty array means parent is managing this)
    // Only fetch if propAssignableUsers is undefined (not provided at all)
    if (propAssignableUsers !== undefined) {
      setAssignableUsers(propAssignableUsers);
      if (propAssignableUsers.length > 0) {
        console.log(`✅ Using ${propAssignableUsers.length} assignable users from props for company ${companyName}`);
      }
      return;
    }

    // Only fetch if parent didn't provide the prop at all
    const loadAssignableUsers = async () => {
      try {
        const users = await userAPI.getAssignableUsers(companyId);
        setAssignableUsers(users);
        console.log(`✅ Loaded ${users.length} assignable users for company ${companyName}`);
      } catch (error) {
        console.error('❌ Error loading assignable users:', error);
      }
    };

    loadAssignableUsers();
  }, [companyId, companyName, propAssignableUsers]);

  // Update items when actionItems prop changes, but preserve editing state
  React.useEffect(() => {
    setItems(prevItems => {
      // If we're currently editing items, preserve their editing state
      const editingItems = prevItems.filter(item => item.isEditing);
      const newItems = actionItems.map(actionItem => {
        const existingEditingItem = editingItems.find(item => item.id === actionItem.id);
        if (existingEditingItem) {
          // Preserve editing state and edited values
          return existingEditingItem;
        }
        return actionItem;
      });
      return newItems;
    });
  }, [actionItems]);

  // Apply filters, sorting, and grouping
  const filteredAndGroupedItems = useMemo(() => {
    let filtered = items;

    // Filter by completion status
    if (!showCompleted) {
      filtered = filtered.filter(item => item.status !== 'completed' && item.status !== 'cancelled');
    }

    // Apply user filter - show items where the user is assigned to the task OR any subtask
    if (userFilter !== '') {
      filtered = filtered.filter(item => {
        // Check if user is assigned to the action item itself
        const isAssignedToTask = item.assigned_to === userFilter;
        
        // Check if user is assigned to any subtask
        const isAssignedToSubtask = item.subtasks?.some(
          subtask => subtask.assigned_to === userFilter
        );
        
        return isAssignedToTask || isAssignedToSubtask;
      });
    }

    // Apply text filters
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(item => 
        item.title.toLowerCase().includes(searchLower) ||
        (item.assigned_to_name || '').toLowerCase().includes(searchLower)
      );
    }

    if (filters.status) {
      filtered = filtered.filter(item => item.status === filters.status);
    }

    if (filters.assignedTo) {
      if (filters.assignedTo === '__BLANK__') {
        // Show items where neither the task nor any subtask has an assignment
        filtered = filtered.filter(item => {
          const taskUnassigned = !item.assigned_to_name || item.assigned_to_name.trim() === '';
          const allSubtasksUnassigned = !item.subtasks?.some(subtask => subtask.assigned_to_name && subtask.assigned_to_name.trim() !== '');
          return taskUnassigned && allSubtasksUnassigned;
        });
      } else {
        // Show items where the task OR any subtask is assigned to this person
        filtered = filtered.filter(item => {
          const isAssignedToTask = item.assigned_to_name === filters.assignedTo;
          const isAssignedToSubtask = item.subtasks?.some(
            subtask => subtask.assigned_to_name === filters.assignedTo
          );
          return isAssignedToTask || isAssignedToSubtask;
        });
      }
    }

    if (filters.audit) {
      if (filters.audit === '__BLANK__') {
        filtered = filtered.filter(item => !item.audit_name || item.audit_name.trim() === '');
      } else {
        filtered = filtered.filter(item => item.audit_name === filters.audit);
      }
    }

    if (filters.priority) {
      filtered = filtered.filter(item => item.priority === filters.priority);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];

      // Handle date fields
      if (sortField === 'start_date' || sortField === 'due_date' || sortField === 'created_at') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }

      // Handle string fields
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = (bValue || '').toLowerCase();
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    // Group by audit name AND site name to keep different sites separate
    const grouped = filtered.reduce((acc, item) => {
      // Create a unique key combining audit name and site name
      const groupKey = item.site_name 
        ? `${item.audit_name} - ${item.site_name}`
        : item.audit_name;
      if (!acc[groupKey]) {
        acc[groupKey] = [];
      }
      acc[groupKey].push(item);
      return acc;
    }, {} as Record<string, EditableActionItem[]>);

    return { grouped, filteredItems: filtered };
  }, [items, showCompleted, filters, sortField, sortDirection, userFilter]);

  // Calculate filtered completion summary
  const filteredCompletionSummary = useMemo(() => {
    const filteredItems = filteredAndGroupedItems.filteredItems;
    
    // Count action items
    const totalActionItems = filteredItems.length;
    const completedActionItems = filteredItems.filter(item => item.status === 'completed').length;
    const inProgressActionItems = filteredItems.filter(item => item.status === 'in_progress').length;
    
    // Count subtasks across all filtered items
    let totalSubtasks = 0;
    let completedSubtasks = 0;
    let inProgressSubtasks = 0;
    
    filteredItems.forEach(item => {
      const subtasks = item.subtasks || [];
      totalSubtasks += subtasks.length;
      completedSubtasks += subtasks.filter(s => s.status === 'completed').length;
      inProgressSubtasks += subtasks.filter(s => s.status === 'in_progress').length;
    });
    
    // Calculate completion percentage based on subtasks (if any) or action items
    const completionPercentage = totalSubtasks > 0
      ? Math.round((completedSubtasks / totalSubtasks) * 100)
      : totalActionItems > 0
        ? Math.round((completedActionItems / totalActionItems) * 100)
        : 0;
    
    return {
      total_action_items: totalActionItems,
      completed_action_items: completedActionItems,
      in_progress_action_items: inProgressActionItems,
      total_subtasks: totalSubtasks,
      completed_subtasks: completedSubtasks,
      in_progress_subtasks: inProgressSubtasks,
      completion_percentage: completionPercentage,
    };
  }, [filteredAndGroupedItems.filteredItems]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return userFilter !== '' || 
           filters.search !== '' || 
           filters.status !== '' || 
           filters.assignedTo !== '' || 
           filters.audit !== '' || 
           filters.priority !== '';
  }, [userFilter, filters]);

  // Extract unique values for dropdown filters
  const uniqueAssignedTo = useMemo(() => {
    const hasBlankAssigned = items.some(item => !item.assigned_to_name || item.assigned_to_name.trim() === '');
    const values = items
      .map(item => item.assigned_to_name)
      .filter(name => name && name.trim() !== '')
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
    
    // Add "Unassigned" option if there are items with no assigned person
    if (hasBlankAssigned) {
      return ['__BLANK__', ...values];
    }
    return values;
  }, [items]);

  const uniqueAudits = useMemo(() => {
    const hasBlankAudit = items.some(item => !item.audit_name || item.audit_name.trim() === '');
    const values = items
      .map(item => item.audit_name)
      .filter(name => name && name.trim() !== '')
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
    
    // Add "No Audit" option if there are items with no audit name
    if (hasBlankAudit) {
      return ['__BLANK__', ...values];
    }
    return values;
  }, [items]);

  const handleEdit = (itemId: number) => {
    setItems(prev => prev.map(item => 
      item.id === itemId 
        ? { ...item, isEditing: true }
        : item
    ));
  };

  const handleSave = async (itemId: number) => {
    try {
      // Get values from DOM inputs
      const titleInput = document.querySelector(`[data-field="${itemId}-title"]`) as HTMLInputElement;
      const startDateInput = document.querySelector(`[data-field="${itemId}-start_date"]`) as HTMLInputElement;
      const dueDateInput = document.querySelector(`[data-field="${itemId}-due_date"]`) as HTMLInputElement;
      const assignedToInput = document.querySelector(`[data-field="${itemId}-assigned_to"]`) as HTMLSelectElement;
      const priorityInput = document.querySelector(`[data-field="${itemId}-priority"]`) as HTMLSelectElement;
      const statusInput = document.querySelector(`[data-field="${itemId}-status"]`) as HTMLSelectElement;

      const updates: Partial<ActionItem> = {};
      if (titleInput?.value !== undefined) updates.title = titleInput.value;
      if (startDateInput?.value) updates.start_date = startDateInput.value;
      if (dueDateInput?.value) updates.due_date = dueDateInput.value;
      if (assignedToInput?.value !== undefined) {
        updates.assigned_to = assignedToInput.value ? parseInt(assignedToInput.value) : null;
      }
      if (priorityInput?.value) updates.priority = priorityInput.value as ActionItem['priority'];
      if (statusInput?.value) updates.status = statusInput.value as ActionItem['status'];

      await aiBuilderAPI.updateActionItem(itemId, updates);
      
      setItems(prev => prev.map(i => 
        i.id === itemId 
          ? { ...i, isEditing: false }
          : i
      ));
      
      // Refresh data from parent
      onActionItemUpdate();
    } catch (error) {
      console.error('Error updating action item:', error);
    }
  };

  const handleCancel = (itemId: number) => {
    setItems(prev => prev.map(item => 
      item.id === itemId 
        ? { ...item, isEditing: false }
        : item
    ));
  };

  // Sorting handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Comment expansion handlers
  const toggleComments = (itemId: number) => {
    setExpandedComments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Filter handlers
  const handleFilterChange = (filterType: keyof typeof filters, value: string) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const clearAllFilters = () => {
    setFilters({ search: '', status: '', assignedTo: '', audit: '', priority: '' });
    setUserFilter('');
  };

  // Delete all tasks handlers
  const handleOpenDeleteDialog = (sessionId: number, auditName: string, itemCount: number) => {
    setDeleteTargetAudit({ sessionId, auditName, itemCount });
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteTargetAudit(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetAudit) return;
    
    try {
      setIsDeleting(true);
      await aiBuilderAPI.deleteActionItemsByAudit(deleteTargetAudit.sessionId);
      console.log(`✅ Deleted all action items for audit: ${deleteTargetAudit.auditName}`);
      handleCloseDeleteDialog();
      onActionItemUpdate(); // Refresh the list
    } catch (error) {
      console.error('❌ Error deleting action items:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Expansion handlers
  const toggleExpanded = async (itemId: number) => {
    const isCurrentlyExpanded = expandedItems.has(itemId);
    
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (isCurrentlyExpanded) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });

    // If expanding and the item can generate sub-tasks (no existing sub-tasks), auto-generate them
    if (!isCurrentlyExpanded) {
      const item = items.find(i => i.id === itemId);
      if (item && item.can_generate_subtasks) {
        try {
          console.log(`Auto-generating sub-tasks for item ${itemId}`);
          setGeneratingItems(prev => new Set(prev).add(itemId));
          await aiBuilderAPI.generateSubTasks(itemId);
          // Refresh the data to show the new sub-tasks
          onActionItemUpdate();
        } catch (error) {
          console.error('Error auto-generating sub-tasks:', error);
          // Don't show error to user - just expand without sub-tasks
        } finally {
          setGeneratingItems(prev => {
            const newSet = new Set(prev);
            newSet.delete(itemId);
            return newSet;
          });
        }
      }
    }
  };

  const handleSubTasksChange = () => {
    // Refresh the parent data
    onActionItemUpdate();
  };

  // Toggle audit section expansion
  const toggleAuditSection = (auditName: string) => {
    setExpandedAuditSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(auditName)) {
        newSet.delete(auditName);
      } else {
        newSet.add(auditName);
      }
      return newSet;
    });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };



  const EditableCell: React.FC<{
    item: EditableActionItem;
    field: keyof ActionItem;
    type: 'text' | 'select' | 'date';
    options?: { value: string; label: string }[];
    multiline?: boolean;
    inputRef?: React.RefObject<HTMLInputElement>;
    dataField?: string;
    displayField?: keyof ActionItem;
  }> = React.memo(({ item, field, type, options, multiline = false, inputRef, dataField, displayField }) => {
    const value = item[field];
    const fieldToDisplay = displayField || field;
    const displayValue = item[fieldToDisplay];
    
    // Handle different field types for display
    let formattedDisplayValue: string | number | undefined;
    if (fieldToDisplay === 'start_date' || fieldToDisplay === 'due_date') {
      formattedDisplayValue = formatDate(displayValue as string);
    } else if (fieldToDisplay === 'subtasks' || fieldToDisplay === 'can_generate_subtasks') {
      // Don't display these fields in editable cells
      formattedDisplayValue = '';
    } else if (type === 'select' && options) {
      // For select fields, show the label instead of the raw value
      const option = options.find(opt => opt.value === displayValue);
      formattedDisplayValue = option ? option.label : displayValue as string;
    } else {
      formattedDisplayValue = displayValue as string | number | undefined;
    }

    if (!item.isEditing) {
      return (
        <Typography variant="body2" sx={{ minHeight: '20px' }}>
          {formattedDisplayValue || '-'}
        </Typography>
      );
    }

    switch (type) {
      case 'select':
        return (
          <FormControl size="small" fullWidth>
            <Select
              defaultValue={value || ''}
              inputRef={inputRef}
              inputProps={{ 'data-field': dataField }}
            >
              {options?.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'date':
        return (
          <TextField
            type="date"
            size="small"
            fullWidth
            defaultValue={value ? new Date(value as string).toISOString().split('T')[0] : ''}
            inputRef={inputRef}
            inputProps={{ 'data-field': dataField }}
            InputLabelProps={{ shrink: true }}
          />
        );
      default:
        return (
          <TextField
            size="small"
            fullWidth
            multiline={multiline}
            maxRows={multiline ? 3 : 1}
            defaultValue={value || ''}
            inputRef={inputRef}
            inputProps={{ 'data-field': dataField }}
          />
        );
    }
  });

  // Sortable header component
  const SortableHeader: React.FC<{ field: SortField; children: React.ReactNode }> = ({ field, children }) => (
    <TableCell>
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortDirection : 'asc'}
        onClick={() => handleSort(field)}
      >
        {children}
      </TableSortLabel>
    </TableCell>
  );

  if (Object.keys(filteredAndGroupedItems.grouped).length === 0) {
    return (
      <Box>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScheduleIcon color="primary" />
              Action Items for {companyName}
            </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showCompleted}
                  onChange={(e) => setShowCompleted(e.target.checked)}
                />
              }
              label="Show completed items"
            />
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              endIcon={showFilters ? <CollapseIcon /> : <ExpandIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filters
            </Button>
          </Box>
        </Box>

        {/* Filters Section */}
        <Collapse in={showFilters}>
          <Card variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                label="Search"
                placeholder="Search tasks or people..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                sx={{ minWidth: 200 }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={filters.status}
                  displayEmpty
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={filters.assignedTo}
                  displayEmpty
                  onChange={(e) => handleFilterChange('assignedTo', e.target.value)}
                >
                  <MenuItem value="">All Assigned</MenuItem>
                  {uniqueAssignedTo.map((person) => (
                    <MenuItem key={person} value={person}>
                      {person === '__BLANK__' ? 'Unassigned' : person}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* User Filter - filters by tasks/subtasks assigned to user */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  value={userFilter}
                  displayEmpty
                  onChange={(e) => setUserFilter(e.target.value as number | '')}
                  startAdornment={<PersonIcon sx={{ color: userFilter !== '' ? 'primary.main' : 'text.secondary', mr: 1 }} />}
                  sx={{
                    bgcolor: userFilter !== '' ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: userFilter !== '' ? 'primary.main' : undefined,
                    }
                  }}
                >
                  <MenuItem value="">All Users</MenuItem>
                  {currentUser && (
                    <MenuItem value={currentUser.id}>
                      <strong>My Tasks</strong>
                    </MenuItem>
                  )}
                  {assignableUsers
                    .filter(u => u.id !== currentUser?.id)
                    .map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={filters.audit}
                  displayEmpty
                  onChange={(e) => handleFilterChange('audit', e.target.value)}
                >
                  <MenuItem value="">All Audits</MenuItem>
                  {uniqueAudits.map((audit) => (
                    <MenuItem key={audit} value={audit}>
                      {audit === '__BLANK__' ? 'No Audit' : audit}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={filters.priority}
                  displayEmpty
                  onChange={(e) => handleFilterChange('priority', e.target.value)}
                >
                  <MenuItem value="">All Priorities</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                onClick={clearAllFilters}
                disabled={!filters.search && !filters.status && !filters.assignedTo && !filters.audit && !filters.priority && userFilter === ''}
              >
                Clear All
              </Button>
            </Box>
          </Card>
        </Collapse>
      </Paper>

      {/* Empty State */}
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No action items found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {userFilter !== '' 
            ? "No tasks or subtasks are assigned to this user. Try selecting a different user or 'All Users'."
            : "Try adjusting your filters or check \"Show completed items\" above."
          }
        </Typography>
      </Paper>
    </Box>
  );
  }

  // Use filtered summary when filters are active, otherwise use the prop summary
  const displaySummary = hasActiveFilters ? filteredCompletionSummary : completionSummary;

  return (
    <Box>
      {/* Completion Summary Card */}
      {displaySummary && displaySummary.total_subtasks > 0 && (
        <Card sx={{ mb: 3, bgcolor: 'background.paper' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                📊 {hasActiveFilters ? 'Filtered Progress' : 'Overall Progress'}
                {hasActiveFilters && (
                  <Chip 
                    label="Filtered" 
                    size="small" 
                    color="primary" 
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
              <Typography 
                variant="h4" 
                sx={{ 
                  color: displaySummary.completion_percentage >= 80 ? 'success.main' : 
                         displaySummary.completion_percentage >= 50 ? 'warning.main' : 'error.main',
                  fontWeight: 'bold'
                }}
              >
                {displaySummary.completion_percentage}%
              </Typography>
            </Box>
            
            <LinearProgress 
              variant="determinate" 
              value={displaySummary.completion_percentage}
              sx={{ 
                height: 12, 
                borderRadius: 6,
                mb: 2,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 6,
                  bgcolor: displaySummary.completion_percentage >= 80 ? 'success.main' : 
                           displaySummary.completion_percentage >= 50 ? 'warning.main' : 'error.main'
                }
              }}
            />
            
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Action Items</Typography>
                <Typography variant="h6">
                  {displaySummary.completed_action_items} / {displaySummary.total_action_items}
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    completed
                  </Typography>
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Sub-tasks</Typography>
                <Typography variant="h6">
                  {displaySummary.completed_subtasks} / {displaySummary.total_subtasks}
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    completed
                  </Typography>
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">In Progress</Typography>
                <Typography variant="h6">
                  {displaySummary.in_progress_action_items} tasks, {displaySummary.in_progress_subtasks} subtasks
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon color="primary" />
            Action Items for {companyName}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showCompleted}
                  onChange={(e) => setShowCompleted(e.target.checked)}
                />
              }
              label="Show completed items"
            />
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              endIcon={showFilters ? <CollapseIcon /> : <ExpandIcon />}
              onClick={() => setShowFilters(!showFilters)}
            >
              Filters
            </Button>
          </Box>
        </Box>

        {/* Filters Section */}
        <Collapse in={showFilters}>
          <Card variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                size="small"
                label="Search"
                placeholder="Search tasks or people..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                sx={{ minWidth: 200 }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={filters.status}
                  displayEmpty
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={filters.assignedTo}
                  displayEmpty
                  onChange={(e) => handleFilterChange('assignedTo', e.target.value)}
                >
                  <MenuItem value="">All Assigned</MenuItem>
                  {uniqueAssignedTo.map((person) => (
                    <MenuItem key={person} value={person}>
                      {person === '__BLANK__' ? 'Unassigned' : person}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* User Filter - filters by tasks/subtasks assigned to user */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  value={userFilter}
                  displayEmpty
                  onChange={(e) => setUserFilter(e.target.value as number | '')}
                  startAdornment={<PersonIcon sx={{ color: userFilter !== '' ? 'primary.main' : 'text.secondary', mr: 1 }} />}
                  sx={{
                    bgcolor: userFilter !== '' ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: userFilter !== '' ? 'primary.main' : undefined,
                    }
                  }}
                >
                  <MenuItem value="">All Users</MenuItem>
                  {currentUser && (
                    <MenuItem value={currentUser.id}>
                      <strong>My Tasks</strong>
                    </MenuItem>
                  )}
                  {assignableUsers
                    .filter(u => u.id !== currentUser?.id)
                    .map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={filters.audit}
                  displayEmpty
                  onChange={(e) => handleFilterChange('audit', e.target.value)}
                >
                  <MenuItem value="">All Audits</MenuItem>
                  {uniqueAudits.map((audit) => (
                    <MenuItem key={audit} value={audit}>
                      {audit === '__BLANK__' ? 'No Audit' : audit}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={filters.priority}
                  displayEmpty
                  onChange={(e) => handleFilterChange('priority', e.target.value)}
                >
                  <MenuItem value="">All Priorities</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                onClick={clearAllFilters}
                disabled={!filters.search && !filters.status && !filters.assignedTo && !filters.audit && !filters.priority && userFilter === ''}
              >
                Clear All
              </Button>
            </Box>
          </Card>
        </Collapse>
      </Paper>

      {/* Action Items by Audit */}
      {Object.entries(filteredAndGroupedItems.grouped).map(([auditName, auditItems]) => {
        // Get the session ID from the first item in this group
        const sessionId = auditItems[0]?.audit_session_id;
        const isExpanded = expandedAuditSections.has(auditName);
        
        return (
        <Paper key={auditName} sx={{ mb: 3 }}>
          <Box 
            sx={{ 
              p: 2, 
              backgroundColor: 'primary.main', 
              color: 'primary.contrastText', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'primary.dark',
              }
            }}
            onClick={() => toggleAuditSection(auditName)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
              <Typography variant="h6">
                {auditName} ({auditItems.length} item{auditItems.length !== 1 ? 's' : ''})
              </Typography>
            </Box>
            {isPrimaryUser && sessionId && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent toggle when clicking delete
                  handleOpenDeleteDialog(sessionId, auditName, auditItems.length);
                }}
                sx={{ 
                  color: 'primary.contrastText', 
                  borderColor: 'primary.contrastText',
                  '&:hover': {
                    borderColor: 'error.light',
                    backgroundColor: 'error.dark',
                    color: 'white'
                  }
                }}
              >
                Delete All Tasks
              </Button>
            )}
          </Box>
          
          <Collapse in={isExpanded}>
          <TableContainer>
                        <Table>
              <TableHead>
                <TableRow>
                  <SortableHeader field="title">Action Item</SortableHeader>
                  <SortableHeader field="start_date">Start Date</SortableHeader>
                  <SortableHeader field="due_date">End Date</SortableHeader>
                  <SortableHeader field="assigned_to_name">Assigned To</SortableHeader>
                  <SortableHeader field="priority">Priority</SortableHeader>
                  <SortableHeader field="status">Status</SortableHeader>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditItems.map((item) => (
                  <React.Fragment key={item.id}>
                    <TableRow hover>
                                          <TableCell sx={{ maxWidth: 400 }}>
                        <EditableCell
                          key={`${item.id}-title`}
                          item={item}
                          field="title"
                          type="text"
                          multiline
                          dataField={`${item.id}-title`}
                        />
                      </TableCell>
                      <TableCell>
                        <EditableCell
                          key={`${item.id}-start_date`}
                          item={item}
                          field="start_date"
                          type="date"
                          dataField={`${item.id}-start_date`}
                        />
                      </TableCell>
                      <TableCell>
                        <EditableCell
                          key={`${item.id}-due_date`}
                          item={item}
                          field="due_date"
                          type="date"
                          dataField={`${item.id}-due_date`}
                        />
                      </TableCell>
                      <TableCell>
                        <EditableCell
                          key={`${item.id}-assigned_to`}
                          item={item}
                          field="assigned_to"
                          type="select"
                          dataField={`${item.id}-assigned_to`}
                          options={[
                            { value: '', label: 'Unassigned' },
                            ...assignableUsers.map(user => ({
                              value: user.id.toString(),
                              label: `${user.first_name} ${user.last_name} (${user.username})`
                            }))
                          ]}
                          displayField="assigned_to_name"
                        />
                      </TableCell>
                      <TableCell>
                        <EditableCell
                          key={`${item.id}-priority`}
                          item={item}
                          field="priority"
                          type="select"
                          dataField={`${item.id}-priority`}
                          options={[
                            { value: 'low', label: 'Low' },
                            { value: 'medium', label: 'Medium' },
                            { value: 'high', label: 'High' },
                            { value: 'critical', label: 'Critical' },
                          ]}
                        />
                      </TableCell>
                      <TableCell>
                        <EditableCell
                          key={`${item.id}-status`}
                          item={item}
                          field="status"
                          type="select"
                          dataField={`${item.id}-status`}
                          options={[
                            { value: 'not_started', label: 'Not Started' },
                            { value: 'pending', label: 'Pending' },
                            { value: 'in_progress', label: 'In Progress' },
                            { value: 'completed', label: 'Completed' },
                            { value: 'cancelled', label: 'Cancelled' },
                          ]}
                        />
                      </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {item.isEditing ? (
                          <>
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleSave(item.id)}
                            >
                              <SaveIcon />
                            </IconButton>
                            <IconButton 
                              size="small"
                              onClick={() => handleCancel(item.id)}
                            >
                              <CancelIcon />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton 
                              size="small"
                              onClick={() => handleEdit(item.id)}
                            >
                              <EditIcon />
                            </IconButton>
                            <CommentSection
                              key={`task-toggle-${item.id}-${commentUpdateTrigger}`}
                              entityType="task"
                              entityId={item.id}
                              onCommentsChange={() => {
                                setCommentUpdateTrigger(prev => prev + 1);
                                onActionItemUpdate();
                              }}
                              showToggleButton={true}
                              isExpanded={expandedComments.has(item.id)}
                              onToggleExpand={() => toggleComments(item.id)}
                              initialCommentCount={item.comment_count}
                            />
                            <IconButton 
                              size="small"
                              onClick={() => {
                                console.log(`Toggling expand for item ${item.id}`, 'Current expanded:', expandedItems);
                                toggleExpanded(item.id);
                              }}
                              color={expandedItems.has(item.id) ? "primary" : "default"}
                            >
                              <ExpandMoreIcon />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </TableCell>
                    </TableRow>
                    {expandedComments.has(item.id) && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ p: 0, bgcolor: 'grey.50' }}>
                          <CommentSection
                            key={`task-expanded-${item.id}-${commentUpdateTrigger}`}
                            entityType="task"
                            entityId={item.id}
                            onCommentsChange={() => {
                              setCommentUpdateTrigger(prev => prev + 1);
                              onActionItemUpdate();
                            }}
                            alwaysExpanded={true}
                            initialCommentCount={item.comment_count}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                    {expandedItems.has(item.id) && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ p: 0 }}>
                                                  <SubTasksTable
                          actionItemId={item.id}
                          subtasks={item.subtasks || []}
                          canGenerate={item.can_generate_subtasks || false}
                          isGenerating={generatingItems.has(item.id)}
                          companyId={companyId}
                          onSubTasksChange={handleSubTasksChange}
                          assignableUsers={assignableUsers}
                          highlightUserId={userFilter !== '' ? userFilter : undefined}
                        />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </Collapse>
        </Paper>
        );
      })}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          ⚠️ Delete All Action Items
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>all {deleteTargetAudit?.itemCount} action items</strong> for the audit <strong>"{deleteTargetAudit?.auditName}"</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'error.main' }}>
            This action cannot be undone. All associated subtasks and comments will also be deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={isDeleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActionItemsManagement; 